import { getStartAnalysis } from "api/layer";
import { AppAction, IAppArguments } from "helpers/context";
import {
  reportHazardLayerStatistics,
  reportPerilLayerStatistics,
} from "helpers/layerStatistics";
import { Dispatch } from "react";

export interface SingleAnalysisParams {
  eventLayers?: string[];
  catNetLayers?: string[];
  responseStructure?: string[];
  climateRiskScores?: Array<{ index: string; scenario?: string }>;
  futureHazardRiskScores?: Array<{ index: string; scenario?: string }>;
  crsComponentRiskScores?: Array<{ index: string; scenario?: string }>;
  longitude?: number | string;
  latitude?: number | string;
  country?: string;
  oversea?: boolean;
  spatial?: string;
}

export function syncSingleAnalysisRunningStatus(
  selectedButtons: string[][],
  dispatch: Dispatch<IAppArguments>,
  existingCategorySspData?: Partial<Record<string, unknown>>
) {
  // Hazard 分析：同步返回结果，直接设置 analysisData 为 "running"
  if (selectedButtons[0]?.length) {
    dispatch({
      type: AppAction.setAnalysisData,
      payload: { legendItems: "running", analysisItems: "running" },
    });
  }

  // Climate 分析：异步返回 batchId，需要分别判断每个类型
  const needCrs = (selectedButtons[1]?.length || 0) > 0;
  const needFutureHazard = (selectedButtons[2]?.length || 0) > 0;
  const needCrsComponent = (selectedButtons[3]?.length || 0) > 0;

  if (existingCategorySspData && (needCrs || needFutureHazard || needCrsComponent)) {
    const nextCategorySspData = { ...existingCategorySspData };

    if (needCrs) {
      delete nextCategorySspData.climate;
    }

    if (needFutureHazard) {
      delete nextCategorySspData.futureHazard;
    }

    if (needCrsComponent) {
      delete nextCategorySspData.CRSComponent;
    }

    dispatch({
      type: AppAction.setClimateRiskCategorySspData,
      payload: Object.keys(nextCategorySspData).length
        ? nextCategorySspData
        : undefined,
    });
  }

  // 仅当本次选择了气候分析时，清空旧数据并设置 loading 状态
  // 未选择的气候类型数据保持不变，从而实现多轮分析结果的独立存储
  if (needCrs) {
    dispatch({ type: AppAction.setSspData, payload: undefined });
    dispatch({ type: AppAction.setBatchId, payload: "running" });
  }

  if (needFutureHazard) {
    dispatch({ type: AppAction.setFutureHazardBatchId, payload: "running" });
  }

  if (needCrsComponent) {
    dispatch({ type: AppAction.setCrsComponentBatchId, payload: "running" });
  }
}

export function executeSingleAnalysisWithReporting(
  params: SingleAnalysisParams,
  selectedButtons: string[][],
  hazardLegends: any[] | undefined,
  dispatch: Dispatch<IAppArguments>
) {
  const needCrs = (selectedButtons[1]?.length || 0) > 0;
  const needFutureHazard = (selectedButtons[2]?.length || 0) > 0;
  const needCrsComponent = (selectedButtons[3]?.length || 0) > 0;
  const needClimate = needCrs || needFutureHazard || needCrsComponent;

  const reportClimatePerils = ({
    crsStatus,
    futureHazardStatus,
    crsComponentStatus,
    executeInterval,
    coordinates,
    traceInfo,
  }: {
    crsStatus: "0" | "1";
    futureHazardStatus: "0" | "1";
    crsComponentStatus: "0" | "1";
    executeInterval: number;
    coordinates: string;
    traceInfo?: string;
  }) => {
    if (needCrs) {
      reportPerilLayerStatistics({
        eventType: "single",
        perils: "Climate/CRS",
        status: crsStatus,
        layerAmount: selectedButtons[1]?.length || 0,
        layers: selectedButtons[1],
        locationAmount: 1,
        executeInterval,
        coordinates,
        messageInfo: params.spatial,
        traceInfo,
      });
    }

    if (needFutureHazard) {
      reportPerilLayerStatistics({
        eventType: "single",
        perils: "Climate/FHR",
        status: futureHazardStatus,
        layerAmount: selectedButtons[2]?.length || 0,
        layers: selectedButtons[2],
        locationAmount: 1,
        executeInterval,
        coordinates,
        messageInfo: params.spatial,
        traceInfo,
      });
    }

    if (needCrsComponent) {
      reportPerilLayerStatistics({
        eventType: "single",
        perils: "Climate/Components",
        status: crsComponentStatus,
        layerAmount: selectedButtons[3]?.length || 0,
        layers: selectedButtons[3],
        locationAmount: 1,
        executeInterval,
        coordinates,
        messageInfo: params.spatial,
        traceInfo,
      });
    }
  };

  const syncClimateBatchIds = ({
    crsBatchId,
    futureHazardBatchId,
    crsComponentBatchId,
  }: {
    crsBatchId?: string;
    futureHazardBatchId?: string;
    crsComponentBatchId?: string;
  }) => {
    if (needCrs) {
      dispatch({
        type: AppAction.setBatchId,
        payload: crsBatchId || "error",
      });
    }
    if (needFutureHazard) {
      dispatch({
        type: AppAction.setFutureHazardBatchId,
        payload: futureHazardBatchId || "error",
      });
    }
    if (needCrsComponent) {
      dispatch({
        type: AppAction.setCrsComponentBatchId,
        payload: crsComponentBatchId || "error",
      });
    }
  };

  const startTime = Date.now();

  getStartAnalysis(params)
    .then((resp: any) => {
      const responseTime = Date.now() - startTime;
      const singleCoordinates = `${params.longitude},${params.latitude}`;

      const crsBatchId = resp?.data?.crs?.batchId;
      const futureHazardBatchId = resp?.data?.futureHazard?.batchId;
      const crsComponentBatchId = resp?.data?.crsComponent?.batchId;

      const climateSuccess =
        (!needCrs || !!crsBatchId) &&
        (!needFutureHazard || !!futureHazardBatchId) &&
        (!needCrsComponent || !!crsComponentBatchId);

      if (selectedButtons[0]?.length) {
        const hazardSuccess = !!resp?.data?.catnet?.results;
        reportHazardLayerStatistics({
          eventType: "single",
          status: hazardSuccess ? "1" : "0",
          layers: selectedButtons[0],
          locationAmount: 1,
          executeInterval: responseTime,
          coordinates: singleCoordinates,
          messageInfo: params.spatial,
          traceInfo: hazardSuccess ? undefined : resp?.message || "error",
        });

        const analysisItems = resp?.data?.catnet?.results;
        const legends = hazardLegends?.filter((item) =>
          selectedButtons[0].includes(item.layerId)
        );
        dispatch({
          type: AppAction.setAnalysisData,
          payload: { legendItems: legends, analysisItems },
        });
      }

      if (needClimate) {
        reportClimatePerils({
          crsStatus: needCrs && !crsBatchId ? "0" : "1",
          futureHazardStatus:
            needFutureHazard && !futureHazardBatchId ? "0" : "1",
          crsComponentStatus:
            needCrsComponent && !crsComponentBatchId ? "0" : "1",
          executeInterval: responseTime,
          coordinates: singleCoordinates,
          traceInfo: climateSuccess ? undefined : resp?.message || "error",
        });
        syncClimateBatchIds({
          crsBatchId,
          futureHazardBatchId,
          crsComponentBatchId,
        });
      }
    })
    .catch((error: any) => {
      const responseTime = Date.now() - startTime;
      const singleCoordinates = `${params.longitude},${params.latitude}`;

      if (selectedButtons[0]?.length) {
        reportHazardLayerStatistics({
          eventType: "single",
          status: "0",
          layers: selectedButtons[0],
          locationAmount: 1,
          executeInterval: responseTime,
          coordinates: singleCoordinates,
          messageInfo: params.spatial,
          traceInfo: error?.message,
        });
        dispatch({
          type: AppAction.setAnalysisData,
          payload: { legendItems: undefined, analysisItems: undefined },
        });
      }

      // 错误时只更新本次选择的气候类型状态，未选择的类型数据保持不变
      if (needClimate) {
        reportClimatePerils({
          crsStatus: needCrs ? "0" : "1",
          futureHazardStatus: needFutureHazard ? "0" : "1",
          crsComponentStatus: needCrsComponent ? "0" : "1",
          executeInterval: responseTime,
          coordinates: singleCoordinates,
          traceInfo: error?.message,
        });

        const errorCrsBatchId = needCrs ? "error" : undefined;
        const errorFutureHazardBatchId = needFutureHazard ? "error" : undefined;
        const errorCrsComponentBatchId = needCrsComponent ? "error" : undefined;
        syncClimateBatchIds({
          crsBatchId: errorCrsBatchId,
          futureHazardBatchId: errorFutureHazardBatchId,
          crsComponentBatchId: errorCrsComponentBatchId,
        });
      }
    });
}
